(() => {
  'use strict';

  const controllers = new WeakMap();
  const compareGroups = new Set(['blood-pressure', 'oxygen', 'nebulizers']);
  const filterTypes = new Set(['all', 'upper-arm', 'wrist', 'arm-in', 'compressor', 'handheld', 'home', 'portable', 'accessories', 'other']);

  function updateSelection(current, model, checked, allowed) {
    const selected = [...new Set(current)].filter((id) => allowed.has(id)).slice(0, 3);
    if (!allowed.has(model)) return { selected, limited: false };
    if (!checked) return { selected: selected.filter((id) => id !== model), limited: false };
    if (selected.includes(model)) return { selected, limited: false };
    if (selected.length === 3) return { selected, limited: true };
    return { selected: [...selected, model], limited: false };
  }

  function matchesFilter(type, filter) {
    return filter === 'all' || type === filter;
  }

  function restoreSelectionState(raw, allowed, itemTypes) {
    try {
      const state = JSON.parse(raw);
      if (!state || state.version !== 1 || !Array.isArray(state.selected) || state.selected.length > 3) return null;
      if (new Set(state.selected).size !== state.selected.length || state.selected.some((id) => typeof id !== 'string' || !allowed.has(id))) return null;
      const filter = filterTypes.has(state.filter) && (state.filter === 'all' || itemTypes.has(state.filter)) ? state.filter : 'all';
      return { selected: [...state.selected], filter };
    } catch (_) {
      return null;
    }
  }

  function compareURL(base, group, selected, origin) {
    if (!compareGroups.has(group) || selected.length < 1 || selected.length > 3 || new Set(selected).size !== selected.length) return null;
    try {
      const url = new URL(base, origin);
      if (url.origin !== new URL(origin).origin || !['http:', 'https:'].includes(url.protocol)) return null;
      url.searchParams.delete('products');
      url.searchParams.set('compare_group', group);
      url.searchParams.set('compare_models', selected.join(','));
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return null;
    }
  }

  function createController(root) {

    const defaults = {
      'none-selected': 'No models selected.',
      'selected-count': 'Models selected: __COUNT__.',
      'maximum-reached': 'Maximum of 3 models reached. Remove one to select another.',
      'shown-count': 'Shown: __COUNT__',
      'showing-count': 'Showing __SHOWN__ of __TOTAL__ items on this page.',
      'remove-selection': 'Remove __MODEL__ from selection',
    };
    const messages = Object.fromEntries(Object.entries(defaults).map(([key, fallback]) =>
      [key, root.getAttribute('data-zl-i18n-' + key) || fallback]));
    const message = (key, values = {}) => messages[key].replace(/__([A-Z]+)__/g,
      (token, name) => Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token);

    const doc = root.ownerDocument;
    const origin = doc.location.origin;
    const abort = new AbortController();
    const items = [...root.querySelectorAll('[data-zl-item]')];
    const buttons = [...root.querySelectorAll('[data-zl-filter]')];
    const filterControls = root.querySelector('[data-zl-filter-controls]');
    const status = root.querySelector('[data-zl-status]');
    const tray = root.querySelector('[data-zl-tray]');
    const selectionList = root.querySelector('[data-zl-selection]');
    const compareLink = root.querySelector('[data-zl-compare-link]');
    const group = root.dataset.zlCompareGroup;
    const base = root.dataset.zlCompareBase;
    const allInputs = [...root.querySelectorAll('[data-zl-compare]')];
    const inputs = allInputs.filter((input) => input.dataset.group === group && input.value !== '' && input.value === input.value.trim());
    const allowed = new Set(inputs.map((input) => input.value));
    const enhancedLabels = [...new Set(inputs.map((input) => input.closest('[data-zl-enhanced]')).filter(Boolean))];
    const groupRecords = [...root.querySelectorAll('[data-zl-group]')].map((element) => ({
      element,
      items: items.filter((item) => item.closest('[data-zl-group]') === element),
      count: element.querySelector('[data-zl-count]'),
      empty: element.querySelector('[data-zl-empty]'),
    }));
    const comparisonEnabled = Boolean(compareGroups.has(group) && base && tray && selectionList && compareLink && allowed.size && compareURL(base, group, [inputs[0].value], origin));
    const itemTypes = new Set(items.map((item) => item.dataset.zlType));
    let activeFilter = 'all';
    let selected = [];
    let destroyed = false;
    // Session-only UI state, scoped to this collection. No customer or cart data.
    const storageKey = `ziqing-collection:v1:${doc.location.pathname}:${group || 'general'}`;
    let restored = null;
    try {
      restored = restoreSelectionState(doc.defaultView.sessionStorage.getItem(storageKey), allowed, itemTypes);
    } catch (_) { /* Storage may be unavailable; filtering and selection still work. */ }
    if (restored) {
      activeFilter = restored.filter;
      selected = comparisonEnabled ? restored.selected : [];
    } else if (comparisonEnabled) {
      inputs.filter((input) => input.checked).forEach((input) => {
        selected = updateSelection(selected, input.value, true, allowed).selected;
      });
    }

    function rememberState() {
      try {
        doc.defaultView.sessionStorage.setItem(storageKey, JSON.stringify({ version: 1, filter: activeFilter, selected }));
      } catch (_) { /* Do not block the UI for storage errors or private browsing. */ }
    }

    function selectionMessage() {
      if (!selected.length) return message('none-selected');
      const selectedText = message('selected-count', { COUNT: selected.length });
      return selected.length === 3 ? `${selectedText} ${message('maximum-reached')}` : selectedText;
    }

    function announce(message) {
      if (status) status.textContent = message;
    }

    function applyFilter(filter, announceChange = true) {
      if (!filterTypes.has(filter) || (filter !== 'all' && !itemTypes.has(filter))) return;
      activeFilter = filter;
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.zlFilter === filter)));
      items.forEach((item) => { item.hidden = !matchesFilter(item.dataset.zlType, filter); });
      groupRecords.forEach((record) => {
        const visibleCount = record.items.filter((item) => !item.hidden).length;
        if (record.count) record.count.textContent = message('shown-count', { COUNT: visibleCount });
        if (record.empty) record.empty.hidden = visibleCount !== 0;
      });
      if (announceChange) {
        const visibleCount = items.filter((item) => !item.hidden).length;
        announce([message('showing-count', { SHOWN: visibleCount, TOTAL: items.length }), comparisonEnabled ? selectionMessage() : ''].filter(Boolean).join(' '));
      }
      rememberState();
    }

    function renderSelection() {
      if (!comparisonEnabled) return;
      inputs.forEach((input) => {
        input.checked = selected.includes(input.value);
        input.disabled = selected.length >= 3 && !input.checked;
      });
      const chips = selected.map((model) => {
        const item = doc.createElement('li');
        const label = doc.createElement('span');
        label.textContent = model;
        const remove = doc.createElement('button');
        remove.type = 'button';
        remove.dataset.zlRemove = '';
        remove.value = model;
        remove.setAttribute('aria-label', message('remove-selection', { MODEL: model }));
        remove.textContent = '×';
        item.append(label, remove);
        return item;
      });
      selectionList.replaceChildren(...chips);
      tray.hidden = selected.length === 0;
      compareLink.href = compareURL(base, group, selected, origin) || base;
      rememberState();
    }

    function focusAfterRemoval(index = 0) {
      const chipButtons = [...selectionList.querySelectorAll('[data-zl-remove]')];
      if (chipButtons.length) {
        chipButtons[Math.min(index, chipButtons.length - 1)].focus();
      } else {
        const next = inputs.find((input) => !input.disabled && !input.closest('[data-zl-item]')?.hidden)
          || buttons.find((button) => button.dataset.zlFilter === activeFilter && !button.hidden);
        next?.focus();
      }
    }

    root.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-zl-filter]');
      if (filter && buttons.includes(filter) && !filter.hidden) {
        event.preventDefault();
        applyFilter(filter.dataset.zlFilter);
        return;
      }
      if (!comparisonEnabled) return;
      const remove = event.target.closest('[data-zl-remove]');
      const clear = event.target.closest('[data-zl-clear]');
      if (remove && selectionList.contains(remove)) {
        event.preventDefault();
        const index = selected.indexOf(remove.value);
        selected = updateSelection(selected, remove.value, false, allowed).selected;
        renderSelection();
        announce(selectionMessage());
        focusAfterRemoval(Math.max(0, index));
      } else if (clear && root.contains(clear)) {
        event.preventDefault();
        selected = [];
        renderSelection();
        announce(selectionMessage());
        focusAfterRemoval();
      }
    }, { signal: abort.signal });

    root.addEventListener('change', (event) => {
      const input = event.target;
      if (!comparisonEnabled || !inputs.includes(input)) return;
      const next = updateSelection(selected, input.value, input.checked, allowed);
      selected = next.selected;
      renderSelection();
      announce(selectionMessage());
    }, { signal: abort.signal });

    buttons.forEach((button) => {
      const type = button.dataset.zlFilter;
      button.hidden = !filterTypes.has(type) || (type !== 'all' && !itemTypes.has(type));
    });
    applyFilter(activeFilter, false);
    if (filterControls) filterControls.hidden = buttons.filter((button) => !button.hidden).length < 2;
    if (comparisonEnabled) {
      renderSelection();
      enhancedLabels.forEach((label) => { label.hidden = false; });
    } else if (tray) {
      tray.hidden = true;
    }
    root.dataset.zlReady = 'true';

    return {
      destroy() {
        if (destroyed) return;
        destroyed = true;
        abort.abort();
        items.forEach((item) => { item.hidden = false; });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.zlFilter === 'all')));
        if (filterControls) filterControls.hidden = true;
        enhancedLabels.forEach((label) => { label.hidden = true; });
        inputs.forEach((input) => { input.disabled = false; input.checked = false; });
        if (tray) tray.hidden = true;
        if (selectionList) selectionList.replaceChildren();
        if (compareLink && base) compareLink.href = base;
        if (status) status.textContent = '';
        delete root.dataset.zlReady;
      },
    };
  }

  function initRoot(root) {
    if (controllers.has(root)) return controllers.get(root);
    const controller = createController(root);
    controllers.set(root, controller);
    return controller;
  }

  function init(scope) {
    if (scope.matches?.('[data-zl-root]')) initRoot(scope);
    scope.querySelectorAll('[data-zl-root]').forEach(initRoot);
  }

  function destroy(scope) {
    const roots = [...scope.querySelectorAll('[data-zl-root]')];
    if (scope.matches?.('[data-zl-root]')) roots.unshift(scope);
    roots.forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  }

  if (typeof window === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = { updateSelection, matchesFilter, restoreSelectionState, compareURL, initRoot, init, destroy };
    return;
  }
  if (window.ZiqingCollection) {
    window.ZiqingCollection.init(document);
    return;
  }
  window.ZiqingCollection = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
